import { LogIn, Menu, X, Palette, Radar } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";

// Theme Switcher Component
const ThemeSwitcher: React.FC = () => {
  const { theme, setTheme, availableThemes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  const currentTheme = availableThemes.find((t) => t.name === theme);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        switcherRef.current &&
        !switcherRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={switcherRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-input transition-all duration-200"
        aria-label="Select theme"
      >
        <span className="text-lg">{currentTheme?.icon}</span>
        <span className="hidden sm:inline text-sm font-medium">
          {currentTheme?.label}
        </span>
        <Palette className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          style={{ backgroundColor: "rgb(var(--color-card))" }}
          className="absolute right-0 top-full mt-2 w-48 border border-border rounded-xl shadow-2xl z-50 overflow-hidden"
          onMouseLeave={() => setIsOpen(false)}
        >
          <ul className="py-1">
            {availableThemes.map((t) => (
              <li key={t.name}>
                <button
                  onClick={() => {
                    setTheme(t.name);
                    setIsOpen(false);
                  }}
                  className={`flex items-center gap-3 w-full px-4 py-2 text-left text-sm transition-colors duration-200 ${
                    theme === t.name
                      ? "bg-primary/20 text-primary"
                      : "text-text-secondary hover:bg-input hover:text-text"
                  }`}
                >
                  <span className="text-lg">{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const headerStyle = {
    backgroundColor: "rgb(var(--color-background))",
  };

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <header
      style={headerStyle}
      className="w-full fixed top-0 left-0 z-50 border-b border-border/50 shadow-sm backdrop-blur-sm bg-opacity-95"
    >
      <div className="w-full flex justify-between items-center py-3 px-4 sm:px-6 lg:px-8">
        {/* Left Side - Logo and Name */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0 group">
          {/* LOGO CONTAINER */}
          <div className="relative flex items-center justify-center w-12 h-12">
            {/* 1. INNER LIGHT (The "Soul"): Bright white core radiating primary color */}
            {/* This sits BEHIND the logo and shines through any transparent pixels */}
            <div
              className="absolute inset-0 rounded-full animate-pulse"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(var(--color-primary), 0.8) 40%, transparent 70%)",
                filter: "blur(8px)",
                opacity: 0.8,
              }}
            ></div>

            {/* 2. THE LOGO: Brightened and Floating */}
            <img
              src="/logo.webp"
              alt="Pilum Finance Logo"
              className="
                relative z-10 
                w-full h-full
                object-contain
                transition-transform duration-300 group-hover:scale-110
              "
              style={{
                // Brightness 1.2: Makes the gold/metal look polished
                // Drop-shadows: Stacked to create a "bloom" effect around the edges
                filter:
                  "brightness(1.2) contrast(1.1) drop-shadow(0 0 2px rgba(255,255,255,0.8)) drop-shadow(0 0 10px rgba(var(--color-primary), 0.5))",
              }}
            />

            {/* 3. SURFACE SHINE (The "Gloss"): Overlay */}
            {/* This sits ON TOP. 'mix-blend-overlay' lights up the pixels underneath, making the center look glowing hot */}
            <div
              className="absolute inset-0 z-20 rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 60%)",
                mixBlendMode: "overlay",
              }}
            ></div>
          </div>

          <div className="flex flex-col">
            <h1 className="text-xl sm:text-2xl font-extrabold text-primary leading-tight tracking-tight drop-shadow-sm group-hover:text-primary-hover transition-colors">
              Pilum Finance
            </h1>
            <p className="text-xs text-text-secondary font-semibold leading-tight tracking-wide">
              DeFi Ecosystem
            </p>
          </div>
        </Link>

        {/* Right Side - Navigation Links (Desktop) */}
        <nav className="hidden lg:flex items-center space-x-4 flex-1 justify-end">
          <Link
            to="/"
            className="text-sm text-text font-medium hover:text-primary transition-all duration-200 hover:scale-105 px-3 py-2 rounded-lg hover:bg-input/50"
          >
            Home
          </Link>
          <a
            href="#ecosystem"
            className="text-sm text-text font-medium hover:text-primary transition-all duration-200 hover:scale-105 px-3 py-2 rounded-lg hover:bg-input/50"
          >
            Ecosystem
          </a>
          <a
            href="#features"
            className="text-sm text-text font-medium hover:text-primary transition-all duration-200 hover:scale-105 px-3 py-2 rounded-lg hover:bg-input/50"
          >
            Features
          </a>
          <a
            href="#contact"
            className="text-sm text-text font-medium hover:text-primary transition-all duration-200 hover:scale-105 px-3 py-2 rounded-lg hover:bg-input/50"
          >
            Contact
          </a>
          <Link
            to="/about"
            className="text-sm text-text font-medium hover:text-primary transition-all duration-200 hover:scale-105 px-3 py-2 rounded-lg hover:bg-input/50"
          >
            About
          </Link>
          <Link
            to="/faq"
            className="text-sm text-text font-medium hover:text-primary transition-all duration-200 hover:scale-105 px-3 py-2 rounded-lg hover:bg-input/50"
          >
            FAQ
          </Link>
          <Link
            to="/tradelab"
            className="text-sm text-text font-bold hover:text-primary transition-all duration-200 hover:scale-105 px-3 py-2 rounded-lg hover:bg-input/50 flex items-center gap-1"
          >
            <Radar className="w-3.5 h-3.5 text-primary" />
            Trade Lab
          </Link>

          <div className="ml-2 border-l border-border pl-4">
            <ThemeSwitcher />
          </div>

          <a
            href={import.meta.env.VITE_DEX_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="relative overflow-hidden inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-bold transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary text-text shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:bg-primary-hover hover:scale-105 focus:ring-primary transform border border-primary-hover group ml-2"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-500"></span>
            <span className="relative z-10 flex items-center">
              Launch DEX
              <LogIn className="w-3.5 h-3.5 ml-2 group-hover:translate-x-0.5 transition-transform duration-200" />
            </span>
          </a>
        </nav>

        {/* --- Mobile Header Area --- */}
        <div className="flex items-center gap-2 lg:hidden">
          <ThemeSwitcher />
          <button
            ref={menuButtonRef}
            className="p-2 text-text hover:text-primary transition-colors duration-200 rounded-lg hover:bg-input/50"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Open menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        {isMenuOpen && (
          <div
            ref={menuRef}
            style={headerStyle}
            className="absolute top-full left-0 right-0 border-b border-border lg:hidden shadow-lg animate-fade-up"
          >
            <div className="flex flex-col p-4 space-y-2">
              <Link
                to="/"
                className="text-text font-medium hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-input/50 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <a
                href="#ecosystem"
                className="text-text font-medium hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-input/50 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Ecosystem
              </a>
              <a
                href="#features"
                className="text-text font-medium hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-input/50 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Features
              </a>
              <a
                href="#contact"
                className="text-text font-medium hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-input/50 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </a>
              <Link
                to="/about"
                className="text-text font-medium hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-input/50 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/faq"
                className="text-text font-medium hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-input/50 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                FAQ
              </Link>
              <Link
                to="/tradelab"
                className="text-text font-bold hover:text-primary transition-colors py-3 px-4 rounded-lg hover:bg-input/50 text-center flex items-center justify-center gap-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <Radar className="w-4 h-4 text-primary" />
                Trade Lab
              </Link>

              <a
                href={import.meta.env.VITE_DEX_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg px-4 py-3 text-sm font-bold transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary text-text shadow-md hover:shadow-lg hover:bg-primary-hover hover:scale-105 focus:ring-primary transform border border-primary-hover w-full mt-3"
                onClick={() => setIsMenuOpen(false)}
              >
                Launch DEX
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
